import { notify } from "@portal/BaseComponents/Notifications";
import { download, downloadReports } from "@portal/api/download";
import { GF_URL } from "@portal/lib/ky/type";
import { getFileName, saveFileInDisk } from "@portal/util/save-file-in-disk";
import { SearchParamsOption } from "ky";
import { useCallback, useState } from "react";

export interface DownloadOptions<T> {
  fileName?: string;
  data?: T;
  searchParams?: SearchParamsOption;
}

export type downloadDocumentTypes<T> = ReturnType<
  typeof useDownloadDocument<T>
>;
const t = {
  success_text: "You will receive the export in your mail shortly",
  error_text: "Reports couldn't be downloaded!",
};
function useDownloadDocument<T>(successText?: string, errorText?: string) {
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(false);

  async function handleDownloadReports(
    url: GF_URL,
    options: DownloadOptions<T>
  ) {
    try {
      setFetching(true);
      // some urls are generated dynamically
      await downloadReports<T>(url, options);
      notify.success(successText ?? t.success_text);
    } catch (error) {
      setError(true);
      notify.error(errorText ?? t.error_text);
      setFetching(false);
    } finally {
      setFetching(false);
    }
  }
  async function handleDownload(url: GF_URL, options: DownloadOptions<T>) {
    try {
      setFetching(true);
      // some urls are generated dynamically
      const fileResponse = await download<T>(url, options);
      const fileName =
        options.fileName ?? getFileName<T>(fileResponse) ?? undefined;

      if (fileResponse) {
        await saveFileInDisk<T>(fileResponse, fileName);
      }
      notify.success(successText ?? t.success_text);
    } catch (error) {
      setError(true);
      notify.error(errorText ?? t.error_text);
      setFetching(false);
    } finally {
      setFetching(false);
    }
  }

  const downloadReportsCallback = useCallback(handleDownloadReports, [
    errorText,
    successText,
  ]);
  const downloadCallback = useCallback(handleDownload, [
    errorText,
    successText,
  ]);

  return { downloadReportsCallback, error, fetching, downloadCallback };
}

export default useDownloadDocument;
